import { DATASET_INITIAL_ID } from 'common/constants';
import { useAppSelector } from 'common/hooks/redux';
import { RootState } from 'common/store';
import { Question, SurveyResult, useGetSurveyQuestionsQuery } from 'common/store/api/question.api';
import { selectEndDate, selectStartDate } from 'common/store/slices/date-filter.slice';
import { useMemo } from 'react';

export interface CombinedQuestion extends Question {
  idGroup: number[];
}

export interface CombinedSurveyResult extends SurveyResult {
  idGroup: number[];
  questions: CombinedQuestion[];
}

interface UseSurveyQuestionsDataArgs {
  request?: string;
  projectId?: string;
  startDate?: string;
  endDate?: string;
}

interface UseSurveyQuestionsData {
  isLoading: boolean;
  error: any;
  isFetching: boolean;
  data: CombinedSurveyResult[];
}

const combineQuestions = (data: Question[]): CombinedQuestion[] => {
  return data.reduce((acc: CombinedQuestion[], item: Question) => {
    const combinedQuestionIndex = acc.findIndex((accItem) => accItem.name === item.name);

    if (combinedQuestionIndex === -1) {
      acc.push({
        ...item,
        idGroup: [item.id],
      });
    } else {
      acc[combinedQuestionIndex] = {
        ...acc[combinedQuestionIndex],
        idGroup: [...acc[combinedQuestionIndex].idGroup, item.id],
      };
    }

    return acc;
  }, []);
};

function useSurveyQuestionsData(
  {
    request,
    startDate: propStartDate,
    endDate: propEndDate,
    projectId,
  }: UseSurveyQuestionsDataArgs = {},
  { skip }: { skip: boolean } = { skip: false }
): UseSurveyQuestionsData {
  const startDate = useAppSelector((state: RootState) =>
    selectStartDate(state, DATASET_INITIAL_ID)
  );
  const endDate = useAppSelector((state: RootState) => selectEndDate(state, DATASET_INITIAL_ID));

  const { data, isLoading, error, isFetching } = useGetSurveyQuestionsQuery(
    {
      request,
      startDate: propStartDate ?? startDate,
      endDate: propEndDate ?? endDate,
      projectId: projectId,
    },
    {
      refetchOnMountOrArgChange: true,
      skip,
    }
  );

  const combinedData = useMemo(
    () =>
      data?.reduce((acc: CombinedSurveyResult[], item: SurveyResult) => {
        const combinedResultIndex = acc.findIndex((accItem) => accItem.name === item.name);

        if (combinedResultIndex === -1) {
          acc.push({
            ...item,
            idGroup: [item.id],
            questions: combineQuestions(item.questions),
          });
        } else {
          acc[combinedResultIndex] = {
            ...acc[combinedResultIndex],
            idGroup: [...acc[combinedResultIndex].idGroup, item.id],
            questions: combineQuestions([...acc[combinedResultIndex].questions, ...item.questions]),
          };
        }

        return acc;
      }, []) ?? [],
    [data]
  );

  return useMemo(
    () => ({
      isLoading,
      error,
      isFetching,
      data: combinedData,
    }),
    [isLoading, error, isFetching, combinedData]
  );
}

export default useSurveyQuestionsData;
